import { useState } from 'react';
import { Download, History, Search, SearchX } from 'lucide-react';
import { toast } from 'sonner';
import { APP } from '@/config';
import { useDebounce } from '@/hooks';
import { useActionLog, useActionLogActors } from '@/services/queries';
import { actionLogService } from '@/services/api/action-log.service';
import { EmptyState, ErrorState } from '@/components/common';
import { downloadBlob } from '@/lib/csv';
import type { ActionLogEntry, ActionLogFilters } from '@/types';
import { EVENT_STYLE } from './event-style';
import { Button } from '@/shared/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Table,
  TableBody,
  TableCard,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';

const PAGE_SIZE = 10;
const PAGE_WINDOW = 3;

/** Identical to `CustomerTable`'s own - the pagination style this table was
 *  explicitly asked to reuse rather than invent a second version of. */
function pageWindow(current: number, totalPages: number, size = PAGE_WINDOW): number[] {
  if (totalPages <= 0) return [];

  let start = Math.max(1, current - 1);
  let end = start + size - 1;
  if (end > totalPages) {
    end = totalPages;
    start = Math.max(1, end - size + 1);
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

function formatStamp(iso: string): string {
  return `${new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })} UTC`;
}

const EVENT_LABEL: Record<ActionLogEntry['eventType'], string> = {
  key_generated: 'key.generated',
  key_regenerated: 'key.regenerated',
  activated: 'license.activated',
  password_reset: 'user.password_reset',
  extended: 'license.extended',
  fingerprint_mismatch_soft: 'fingerprint.soft_mismatch',
  fingerprint_mismatch_hard: 'fingerprint.hard_mismatch',
  grace_started: 'grace.started',
  grace_expired: 'grace.expired',
  blocked: 'license.blocked',
  unblocked: 'license.unblocked',
  released: 'license.released',
  revoked_by_admin: 'license.revoked',
  revoked_by_server: 'license.auto_revoked',
  expired: 'license.expired',
};

type EventType = ActionLogEntry['eventType'];
type Period = NonNullable<ActionLogFilters['period']>;

/** `'all'` is a UI sentinel, not a server value - Radix Select refuses an
 *  empty-string item, and `undefined` is what the query wants when nothing is
 *  chosen. Same convention as `CustomerTable`'s filters. */
type EventTypeFilter = EventType | 'all';

const PERIOD_LABEL: Record<Period, string> = {
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  all: 'All (400 days)',
};

/** The design opens on the last week, not the whole retention window. */
const DEFAULT_PERIOD: Period = '7d';

const ACTION_OUTLINE =
  'inline-flex h-9 items-center gap-1.75 rounded-lg border border-input bg-card px-3.75 text-[12.5px] font-semibold whitespace-nowrap text-foreground transition-colors hover:border-primary disabled:pointer-events-none disabled:opacity-60';

function ActionTypeChip({ eventType }: { eventType: ActionLogEntry['eventType'] }) {
  const style = EVENT_STYLE[eventType];
  return (
    <span
      className="inline-flex min-w-37.5 items-center justify-center rounded-[5px] px-2.25 py-0.75 text-center font-mono text-[11px] font-semibold whitespace-nowrap"
      style={{ background: style.bg, color: style.fg }}
    >
      {EVENT_LABEL[eventType]}
    </span>
  );
}

function ActorCell({ entry }: { entry: ActionLogEntry }) {
  const style = EVENT_STYLE[entry.eventType];
  const Icon = style.icon;
  return (
    <div className="flex min-w-0 items-center gap-2.25">
      <span
        className="flex size-6 flex-none items-center justify-center rounded-[7px]"
        style={{ background: style.bg, color: style.fg }}
      >
        <Icon size={13} strokeWidth={1.9} aria-hidden="true" />
      </span>
      <span className="min-w-0 truncate text-[12.5px] font-medium">
        {entry.actorName ?? 'System'}
      </span>
    </div>
  );
}

export function ActionLogTable() {
  const [search, setSearch] = useState('');
  const [eventType, setEventType] = useState<EventTypeFilter>('all');
  const [actor, setActor] = useState('all');
  const [period, setPeriod] = useState<Period>(DEFAULT_PERIOD);
  const [offset, setOffset] = useState(0);
  const [exporting, setExporting] = useState(false);

  const term = useDebounce(search, APP.ui.debounceMs).trim();

  /** Built once so the table and the export read off the same thing - the
   *  file must hold exactly what the filtered table is showing, every page
   *  of it. */
  const filters: ActionLogFilters = {
    ...(term ? { search: term } : {}),
    ...(eventType !== 'all' ? { eventType } : {}),
    ...(actor !== 'all' ? { actor } : {}),
    ...(period !== 'all' ? { period } : {}),
  };
  const isFiltered = Object.keys(filters).length > 0;

  const query = useActionLog(filters, offset, PAGE_SIZE);
  const actors = useActionLogActors();

  const rows = query.data?.data;
  const total = query.data?.meta.total ?? 0;
  const isEmpty = rows !== undefined && rows.length === 0;

  // Page 3 of the old results is meaningless against new ones.
  const onSearch = (value: string) => {
    setSearch(value);
    setOffset(0);
  };
  const onEventType = (value: string) => {
    setEventType(value as EventTypeFilter);
    setOffset(0);
  };
  const onActor = (value: string) => {
    setActor(value);
    setOffset(0);
  };
  const onPeriod = (value: string) => {
    setPeriod(value as Period);
    setOffset(0);
  };

  const clearFilters = () => {
    setSearch('');
    setEventType('all');
    setActor('all');
    setPeriod('all');
    setOffset(0);
  };

  const exportTrail = () => {
    setExporting(true);
    actionLogService
      .exportCsv(filters)
      .then(({ blob, filename }) => {
        downloadBlob(filename, blob);
        toast.success(`Exported ${String(total)} ${total === 1 ? 'entry' : 'entries'}.`);
      })
      .catch((error: unknown) => {
        toast.error(error instanceof Error ? error.message : 'Could not export the action log.');
      })
      .finally(() => {
        setExporting(false);
      });
  };

  return (
    <div className="space-y-3.5">
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="relative min-w-60 flex-1 sm:max-w-100">
          <Search
            size={15}
            strokeWidth={2}
            className="absolute top-1/2 left-2.75 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            className="h-9 w-full rounded-lg border border-input bg-card pr-3 pl-8.25 text-[12.5px] outline-none placeholder:text-muted-foreground focus-visible:border-ring"
            placeholder="Search actor, action or target"
            value={search}
            onChange={(event) => {
              onSearch(event.target.value);
            }}
            aria-label="Search the action log"
          />
        </div>

        <Select value={eventType} onValueChange={onEventType}>
          <SelectTrigger
            size="sm"
            className="w-48 bg-white text-[12.5px]"
            aria-label="Filter by action type"
          >
            <SelectValue placeholder="All action types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All action types</SelectItem>
            {(Object.keys(EVENT_LABEL) as EventType[]).map((type) => (
              <SelectItem key={type} value={type} className="font-mono text-[12px]">
                {EVENT_LABEL[type]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={actor} onValueChange={onActor}>
          <SelectTrigger
            size="sm"
            className="w-36 bg-white text-[12.5px]"
            aria-label="Filter by actor"
          >
            <SelectValue placeholder="All actors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actors</SelectItem>
            <SelectItem value="system">System</SelectItem>
            {actors.data?.map((admin) => (
              <SelectItem key={admin.id} value={admin.id}>
                {admin.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={period} onValueChange={onPeriod}>
          <SelectTrigger
            size="sm"
            className="w-36 bg-white text-[12.5px]"
            aria-label="Filter by date"
          >
            <SelectValue placeholder={PERIOD_LABEL[DEFAULT_PERIOD]} />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(PERIOD_LABEL) as Period[]).map((value) => (
              <SelectItem key={value} value={value}>
                {PERIOD_LABEL[value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex-1" />

        <button
          type="button"
          onClick={exportTrail}
          // A header row and nothing under it is not a useful answer to
          // "export what I am looking at" when the table already says empty.
          disabled={exporting || isEmpty || query.isPending}
          className={ACTION_OUTLINE}
        >
          <Download size={13} strokeWidth={2} aria-hidden="true" />
          {exporting ? 'Exporting…' : 'Export audit trail'}
        </button>
      </div>

      <TableCard>
        {query.isError ? (
          <div className="p-4">
            <ErrorState
              title="Could not load the action log"
              description={query.error.message}
              action={
                <Button
                  variant="outline"
                  onClick={() => {
                    void query.refetch();
                  }}
                >
                  Try again
                </Button>
              }
            />
          </div>
        ) : isEmpty && isFiltered ? (
          <EmptyState
            icon={SearchX}
            title="No actions match these filters"
            description={
              term
                ? `Nothing matches “${term}” with these filters. Widen the date range, or clear everything to see the full log.`
                : 'Nothing was logged with these filters. Widen the date range, or clear everything to see the full log.'
            }
            actions={
              <Button variant="outline" onClick={clearFilters}>
                Clear filters
              </Button>
            }
          />
        ) : isEmpty ? (
          <EmptyState
            icon={History}
            title="No action logged yet"
            description="Every reset, revoke, extend and key issue any admin performs will show up here, newest first."
          />
        ) : (
          <>
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[20%]">Actor</TableHead>
                  <TableHead className="w-[18%]">Action type</TableHead>
                  <TableHead className="w-[20%]">Target</TableHead>
                  <TableHead className="w-[27%]">Detail</TableHead>
                  <TableHead className="w-[15%]">Timestamp</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.isPending &&
                  Array.from({ length: PAGE_SIZE }, (_, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={5}>
                        <Skeleton className="h-5 w-full" />
                      </TableCell>
                    </TableRow>
                  ))}

                {rows?.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      <ActorCell entry={entry} />
                    </TableCell>
                    <TableCell>
                      <ActionTypeChip eventType={entry.eventType} />
                    </TableCell>
                    <TableCell className="overflow-hidden text-[12.5px] font-medium text-ellipsis">
                      {entry.organizationName}
                    </TableCell>
                    <TableCell className="overflow-hidden font-mono text-[11px] text-ellipsis text-muted-foreground">
                      {entry.description}
                    </TableCell>
                    <TableCell className="font-mono text-[11.5px] text-muted-foreground">
                      {formatStamp(entry.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {query.data && total > PAGE_SIZE && (
              <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-[11.5px] text-muted-foreground">
                <span>
                  {`Showing ${String(offset + 1)}–${String(offset + (rows?.length ?? 0))} of ${String(total)} entries · retained 400 days`}
                </span>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={offset === 0}
                    onClick={() => {
                      setOffset(Math.max(0, offset - PAGE_SIZE));
                    }}
                  >
                    Previous
                  </Button>

                  {(() => {
                    const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
                    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

                    return pageWindow(currentPage, totalPages).map((page) => (
                      <Button
                        key={page}
                        variant={page === currentPage ? 'default' : 'outline'}
                        size="icon-sm"
                        aria-current={page === currentPage ? 'page' : undefined}
                        onClick={() => {
                          setOffset((page - 1) * PAGE_SIZE);
                        }}
                      >
                        {page}
                      </Button>
                    ));
                  })()}

                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!query.data.meta.hasMore}
                    onClick={() => {
                      setOffset(offset + PAGE_SIZE);
                    }}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </TableCard>
    </div>
  );
}
